import { Box, Drawer, Stack } from "@mui/material";
import SideBar, { sideBarWidth } from "../organisms/SideBar";
import TopBar from "../organisms/TopBar";
import useBreakpoint from "../../hooks/useBreakpoint";
import { useGlobalContext } from "../../stores/Global";
import { Route, Routes } from "react-router";
import DashboardPage from "../pages/Dashboard";
import StoragePage from "../pages/Storage";
import TextEditorPage from "../pages/Storage/TextEditorPage";
import TicketsPage from "../pages/Tickets";
import CreateManagementTicketPage from "../pages/Tickets/CreateManagementTicket";
import ManageTicketPage from "../pages/Tickets/ManageTicket";
import DepartmentsPage from "../pages/Departments";
import EditDepartmentPage from "../pages/Departments/EditDepartment";
import UsersPage from "../pages/Users";
import SettingsPage from "../pages/Settings";
import SettingsLayout from "../pages/Settings/SettingsLayout";
import EditUserPage from "../pages/Users/EditUser";
import MessagesSettingsPage from "../pages/Settings/MessageSettings";
import ForwardEmailSettingsPage from "../pages/Settings/ForwardEmailSettings";
import SmtpSettingsPage from "../pages/Settings/SmtpSettings";
import OpenAISettingsPage from "../pages/Settings/OpenAISettings";
import FtpSettingsPage from "../pages/Settings/FtpSettings";
import IntegrationSettingsPage from "../pages/Settings/IntegrationSettings";
import ImportSettingsPage from "../pages/Settings/ImportSettings";
import BusinessPage from "../pages/Business";
import { useQuery } from "@tanstack/react-query";
import { useBusinessStore } from "../../stores/Dummy";
import { CompanyService } from "../../services/api";
import SecuritySettingsPage from "../pages/Settings/SecuritySettings";
import LogsSettingsPage from "../pages/Settings/LogsSettings";
import { useEffect } from "react";
import { useNavigate } from "react-router";

interface ManagementTemplateProps {}

export default function ManagementTemplate(props: ManagementTemplateProps) {
  const { context, setContext } = useGlobalContext();
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();
  const sessionChecked = Boolean(context.sessionChecked);

  // Auth guard: once the initial session check has settled, anyone without
  // a user (guests, or accounts deleted while their token was stored) is
  // routed out of the management area to the public landing page.
  useEffect(() => {
    if (sessionChecked && !context.user) {
      navigate("/home", { replace: true });
    }
  }, [sessionChecked, context.user, navigate]);

  const setMiniCompanyList = useBusinessStore(
    (state) => state.setMiniCompanyList,
  );

  useQuery({
    queryKey: ["mini-companies"],
    queryFn: async () => {
      const res = await CompanyService.getCompanyMiniList({
        search: "",
      });
      if (res?.data?.data) {
        const array = res.data.data.sort((a, b) => (a.name > b.name ? 1 : -1));
        setMiniCompanyList(array);
        return array;
      }
      return [];
    },
    refetchInterval: 30000,
  });

  return (
    <Stack height={1} maxWidth={1} bgcolor="background.paper">
      {!breakpoint.md && (
        <Drawer
          open={context.sidebar}
          onClose={() => setContext({ ...context, sidebar: false })}
          elevation={4}
          anchor="left"
          variant="temporary"
          sx={{
            transitionDuration: "200ms",
            width: context.sidebar ? sideBarWidth : 0,
            flexShrink: 0,
            [`> .MuiDrawer-Paper`]: {
              height: "100%",
              background: (theme) =>
                breakpoint.md ? theme.palette.background.paper : "transparent",
              borderRight: "none",
              position: "relative",
              width: sideBarWidth,
            },
          }}
        >
          <Box height={document.getElementById("g-topbar")?.clientHeight} />
          <SideBar />
        </Drawer>
      )}
      <TopBar />
      <Box display="flex" flexGrow={1} overflow="hidden">
        {breakpoint.md && (
          <Box bgcolor="background.default" height={1}>
            <Box
              height={1}
              // width={context.sidebar ? sideBarWidth : undefined}
              display="flex"
              flexDirection="column"
              sx={{
                transitionDuration: "200ms",
                transitionTimingFunction: "ease-in-out",
              }}
            >
              <SideBar />
            </Box>
          </Box>
        )}
        <Box
          flexGrow={1}
          bgcolor="background.paper"
          height={1}
          width={1}
          overflow="hidden"
          p={1}
        >
          <Routes>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="storage">
              <Route index element={<StoragePage />} />
              <Route path="text-editor" element={<TextEditorPage />} />
            </Route>
            <Route path="ticket">
              <Route index element={<TicketsPage />} />
              <Route path="create" element={<CreateManagementTicketPage />} />
              <Route path=":_id" element={<ManageTicketPage />} />
            </Route>
            <Route path="department">
              <Route index element={<DepartmentsPage />} />
              <Route path=":_id" element={<EditDepartmentPage />} />
            </Route>
            <Route path="user">
              <Route index element={<UsersPage />} />
              <Route path=":_id" element={<EditUserPage />} />
            </Route>
            <Route path="settings">
              <Route element={<SettingsLayout />}>
                <Route index element={<SettingsPage />} />
                <Route path="messages" element={<MessagesSettingsPage />} />
                <Route path="forwards" element={<ForwardEmailSettingsPage />} />
                <Route path="smtp" element={<SmtpSettingsPage />} />
                <Route path="openai" element={<OpenAISettingsPage />} />
                <Route path="ftp" element={<FtpSettingsPage />} />
                <Route path="import" element={<ImportSettingsPage />} />
                <Route
                  path="integrations/*"
                  element={<IntegrationSettingsPage />}
                />
                <Route
                  path="ticket-print-template/list"
                  element={<SettingsPage />}
                />
                <Route
                  path="ticket-print-template/create"
                  element={<SettingsPage />}
                />
                <Route
                  path="ticket-print-template/:_id"
                  element={<SettingsPage />}
                />
                <Route path="cities" element={<SettingsPage />} />
                <Route path="branding" element={<SettingsPage />} />
                <Route path="security">
                  <Route index element={<SecuritySettingsPage />} />
                  <Route path=":_id" element={<SecuritySettingsPage />} />
                </Route>
                <Route path="logs" element={<LogsSettingsPage />} />
              </Route>
            </Route>
            <Route path="business/*" element={<BusinessPage />} />
          </Routes>
        </Box>
      </Box>
    </Stack>
  );
}
